/**
 * RenameContext.tsx
 * This file defines a context for managing rename jobs in the application.
 * It provides a way to track the status and progress of rename operations.
 * The context includes actions to start, update, finish, and clear rename jobs.
 *
 */
import React, { createContext, useReducer, useContext, ReactNode } from 'react';
import { JobStatusType } from '../../../api-client/autogenerated';

export interface RenameJob {
  jobId: string;
  status: JobStatusType;
  percent: number;
  // Track whether this is just a progress update to help components optimize rendering
  isProgressUpdate?: boolean;
}

type RenameState = {
  [folderKey: string]: RenameJob;
};

type RenameAction =
  | { type: 'START_RENAME'; payload: { folderKey: string; jobId: string } }
  | { type: 'UPDATE_RENAME_PROGRESS'; payload: { folderKey: string; percent: number } }
  | { type: 'FINISH_RENAME'; payload: { folderKey: string; status: JobStatusType } }
  | { type: 'CLEAR_RENAME'; payload: { folderKey: string; jobId: string } };

const RenameContext = createContext<{
  state: RenameState;
  dispatch: React.Dispatch<RenameAction>;
} | null>(null);

function renameReducer(state: RenameState, action: RenameAction): RenameState {
  switch (action.type) {
    case 'START_RENAME':
      return {
        ...state,
        [action.payload.folderKey]: {
          jobId: action.payload.jobId,
          status: JobStatusType.Initiated,
          percent: 0,
          isProgressUpdate: false,
        },
      };
    case 'UPDATE_RENAME_PROGRESS':
      return {
        ...state,
        [action.payload.folderKey]: {
          ...state[action.payload.folderKey],
          percent: action.payload.percent,
          isProgressUpdate: true,
        },
      };
    case 'FINISH_RENAME':
      return {
        ...state,
        [action.payload.folderKey]: {
          ...state[action.payload.folderKey],
          status: action.payload.status,
          percent: 100,
          isProgressUpdate: false,
        },
      };
    case 'CLEAR_RENAME':
      const { folderKey, jobId } = action.payload;
      // Only clear if we are clearing the SAME job we started
      if (state[folderKey]?.jobId !== jobId) return state;
      const { [action.payload.folderKey]: _, ...rest } = state;
      return rest;
    default:
      return state;
  }
}

export const RenameProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(renameReducer, {});
  return <RenameContext.Provider value={{ state, dispatch }}>{children}</RenameContext.Provider>;
};

export const useRenameContext = () => {
  const context = useContext(RenameContext);
  if (!context) {
    throw new Error('useRenameContext must be used within a RenameProvider');
  }
  return context;
};
